/* ────────────────────────────────────────────────────────────────
   TrainerBookingModal.tsx (updated)
   © playclub
───────────────────────────────────────────────────────────────── */

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Users,
  Dumbbell,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useActivityCreation } from '@/hooks/useActivityCreation';
import { format, isBefore, isSameDay } from 'date-fns';

interface Session {
  id: string;
  title: string;
  description?: string;
  price_cents: number;
  duration_min: number;
  price_mode?: 'fixed' | 'per_person';
  group_min?: number;
  group_max?: number;
}

interface Trainer {
  id: string;
  authId: string;
  name: string;
  city: string;
  image: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  trainer: Trainer;
  preSelectedSession?: Session | null;
  sessions: Session[];
}

const TrainerBookingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  trainer,
  preSelectedSession,
  sessions,
}) => {
  const { isDarkMode } = useTheme();
  const {
    isSubmitting,
    isSuccess,
    handleCreateActivity,
  } = useActivityCreation();

  const [step, setStep] = useState<number>(preSelectedSession ? 2 : 1);
  const [selectedSession, setSelectedSession] = useState<Session | null>(
    preSelectedSession || null,
  );
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (checkoutUrl) {
      setRedirecting(true);
      const timeout = setTimeout(() => {
        window.location.href = checkoutUrl;
      }, 5000); // 5 second delay before redirect
      return () => clearTimeout(timeout);
    }
  }, [checkoutUrl]);

  useEffect(() => {
    if (isOpen) {
      setStep(preSelectedSession ? 2 : 1);
      setSelectedSession(preSelectedSession || null);
      setSelectedDate('');
      setSelectedTime('');
      setNotes('');
      setCurrentMonth(new Date());
    }
  }, [isOpen, preSelectedSession]);

  const formatPrice = (cents: number, mode?: string) => {
    const price = (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    });
    return mode === 'per_person' ? `${price}/person` : price;
  };

  const daysInMonth = (date: Date): (Date | null)[] => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const days: (Date | null)[] = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(y, m, d));
    return days;
  };

  const isPast = (d: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isBefore(d, today);
  };

  const isStepValid = () =>
    (step === 1 && !!selectedSession) ||
    (step === 2 && !!selectedDate) ||
    (step === 3 && !!selectedTime) ||
    step === 4;

  const handleSubmit = async () => {
    if (!selectedSession || !selectedDate || !selectedTime) return;

    const newActivity = await handleCreateActivity({
      opponent: {
        id: trainer.authId,
        name: trainer.name,
        image: trainer.image,
      },
      sport: selectedSession.title,
      date: selectedDate,
      time: selectedTime,
      venue: {
        name: 'To be determined',
        address: 'To be determined',
        city: trainer.city,
      },
      status: 'new_request',
      direction: 'sent',
      notes,
      requiresPayment: true,
      trainer_session_type_id: selectedSession.id,
    });

    const activityId = newActivity.id;

    const res = await fetch('/.netlify/functions/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        activityId,
        sessionId: selectedSession.id,
        trainerId: trainer.id,
      }),
    });

    if (!res.ok) {
      console.error('Checkout error:', await res.text());
      return;
    }

    const { url } = await res.json();
    setCheckoutUrl(url);
  };

  const next = () => (step < 4 ? setStep(step + 1) : handleSubmit());
  const back = () => step > 1 && setStep(step - 1);

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="h-8 w-8 text-dark-50" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Booking Request Sent!</h3>
      <p className="text-dark-800 mb-3">
        Your booking request has been sent to {trainer.name}. You’ll be notified once
        they respond.
      </p>
      <p className="text-dark-800 mb-3">
        You will now be forwarded to Stripe to finalize payment.
      </p>
      <Button 
        disabled={true}
        className="bg-gold-400 text-dark-50 cursor-not-allowed opacity-90">
        Redirecting to Stripe...
      </Button>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Select Session Type</h3>
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setSelectedSession(session)}
                  className={cn(
                    "p-4 rounded-xl cursor-pointer transition-colors",
                    selectedSession?.id === session.id
                      ? "bg-dark-300 border-2 border-gold-400"
                      : "bg-dark-300 border border-dark-400 hover:border-gold-400"
                  )}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-white">{session.title}</h4>
                      {session.description && (
                        <p className="text-dark-800 text-sm mt-1">{session.description}</p>
                      )}
                      <div className="flex items-center mt-2">
                        <Clock className="h-4 w-4 text-dark-800 mr-1" />
                        <span className="text-dark-800 text-sm">{session.duration_min} minutes</span>
                      </div>
                      {session.price_mode === 'per_person' && session.group_min && session.group_max && (
                        <div className="flex items-center mt-1">
                          <Users className="h-4 w-4 text-dark-800 mr-1" />
                          <span className="text-dark-800 text-sm">
                            {session.group_min}-{session.group_max} people
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-gold-400 font-bold">
                      {formatPrice(session.price_cents, session.price_mode)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Select Date</h3>
            <div className="bg-dark-300 rounded-xl p-4">
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="p-2 text-dark-800 hover:text-white transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h4 className="text-white font-medium">
                  {format(currentMonth, 'MMMM yyyy')}
                </h4>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="p-2 text-dark-800 hover:text-white transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <div key={day} className="text-dark-800 text-sm py-1">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {daysInMonth(currentMonth).map((day, i) => (
                  <div
                    key={i}
                    onClick={() => day && !isPast(day) && setSelectedDate(format(day, 'yyyy-MM-dd'))}
                    className={cn(
                      "h-10 flex items-center justify-center rounded-lg",
                      !day && "text-dark-800",
                      day && isPast(day) && "text-dark-800 cursor-not-allowed",
                      day && !isPast(day) && "cursor-pointer hover:bg-dark-400 text-white",
                      day && selectedDate === format(day, 'yyyy-MM-dd') && "bg-gold-400 text-dark-50 hover:bg-gold-500",
                      day && isSameDay(day, new Date()) && !selectedDate && "border border-gold-400"
                    )}
                  >
                    {day ? format(day, 'd') : ''}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Select Time</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'].map((time) => (
                <div
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={cn(
                    "p-3 rounded-lg text-center cursor-pointer transition-colors",
                    selectedTime === time
                      ? "bg-gold-400 text-dark-50"
                      : "bg-dark-300 text-white hover:bg-dark-400"
                  )}
                >
                  {time}
                </div>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Confirm Booking</h3>
            <div className="bg-dark-300 rounded-xl p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-dark-800">Session:</span>
                <span className="text-white font-medium">{selectedSession?.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-800">Date:</span>
                <span className="text-white font-medium">
                  {selectedDate && format(new Date(selectedDate), 'MMMM d, yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-800">Time:</span>
                <span className="text-white font-medium">{selectedTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-800">Price:</span>
                <span className="text-gold-400 font-bold">
                  {selectedSession && formatPrice(selectedSession.price_cents, selectedSession.price_mode)}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-dark-800 mb-2">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requests or information for the trainer..."
                className="w-full bg-dark-300 border border-dark-400 rounded-xl p-3 text-white placeholder-dark-800 resize-none h-24"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-dark-200 border-dark-300 text-white">
        <DialogHeader>
          <DialogTitle>
            {isSuccess ? 'Booking Confirmed' : 'Book a Session'}
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          renderSuccess()
        ) : (
          <div className="space-y-6">
            <div className="w-full h-1 bg-dark-300 rounded-full overflow-hidden">
              <div className="h-full bg-gold-400 transition-all duration-300"
                   style={{ width: `${(step / 4) * 100}%` }} />
            </div>

            {renderStep()}

            {step === 4 && (
              <p className="text-xs text-dark-800 text-center px-2">
                By clicking confirm, you agree to our Terms & Conditions. You will only be charged when the trainer accepts your session. If declined or canceled, you’ll be fully refunded.
              </p>
            )}

            <div className="flex justify-between items-center pt-4">
              {step > 1 ? (
                <Button onClick={back} variant="outline"
                        className="border-dark-400" disabled={isSubmitting}>
                  <ChevronLeft className="h-4 w-4 mr-2" /> Back
                </Button>
              ) : <div />}

              <div className="flex items-center space-x-1 text-dark-800">
                <span className="font-medium text-gold-400">{step}</span>
                <span>/</span>
                <span>4</span>
              </div>

              <Button
                onClick={next}
                disabled={!isStepValid() || isSubmitting}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : step === 4 ? 'Confirm Booking' : (
                  <>Next <ChevronRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrainerBookingModal;
